import api from './api';

const DIRECT_FUNCTIONS_URL = import.meta.env.VITE_FUNCTIONS_URL || 'https://api-offmufdgla-uc.a.run.app';

export interface GeneratedProductCopy {
    seoTitle: string;
    seoDescription: string;
    description: string;
}

export interface GeneratedImageVariant {
    url: string;
    model: string;
    mimeType: string;
    variantKey?: string;
    variantLabel?: string;
}

function getDirectAuthHeaders() {
    const token = localStorage.getItem('authToken');
    return token
        ? {
            Authorization: `Bearer ${token}`,
        }
        : undefined;
}

export const aiApi = {
    async generateSeo(data: {
        title: string;
        description?: string;
        type: 'seoTitle' | 'seoDescription' | 'description';
        entityType?: 'product' | 'category';
    }): Promise<string> {
        const response = await api.post('/admin/ai/generate-seo', data);
        return response.data.result;
    },

    async enhanceProductImage(data: {
        imageUrl: string;
        title: string;
        description?: string;
        addBrandBadge?: boolean;
        variantCount?: number;
    }): Promise<{ images: GeneratedImageVariant[]; count: number; model: string }> {
        const response = await api.post(
            `${DIRECT_FUNCTIONS_URL}/admin/ai/enhance-product-image`,
            data,
            {
                timeout: 180000,
                headers: getDirectAuthHeaders(),
            }
        );

        if (Array.isArray(response.data?.images)) {
            return response.data;
        }

        return {
            images: [response.data],
            count: 1,
            model: response.data?.model || 'gemini-2.5-flash-image',
        };
    },

    async generateProductCopy(data: {
        title: string;
        description?: string;
        imageUrl: string;
        tags?: string[];
    }): Promise<GeneratedProductCopy> {
        const response = await api.post(
            `${DIRECT_FUNCTIONS_URL}/admin/ai/generate-product-copy`,
            data,
            {
                timeout: 180000,
                headers: getDirectAuthHeaders(),
            }
        );
        return response.data;
    },

    async generateBlogPost(data: {
        prompt: string;
        titleHint?: string;
        currentDescription?: string;
        keywords?: string[];
        businessContext?: string;
    }): Promise<{
        title: string;
        suggestedSlug: string;
        excerpt: string;
        seoTitle: string;
        seoDescription: string;
        tags: string[];
        content: string;
    }> {
        const response = await api.post(
            `${DIRECT_FUNCTIONS_URL}/admin/ai/generate-blog-post`,
            data,
            {
                timeout: 180000,
                headers: getDirectAuthHeaders(),
            }
        );
        return response.data;
    },

    async reviseBlogPost(data: {
        revisionPrompt: string;
        currentDraft: string;
        currentTitle?: string;
        currentExcerpt?: string;
        currentSeoTitle?: string;
        currentSeoDescription?: string;
        keywords?: string[];
    }): Promise<{
        title: string;
        excerpt: string;
        seoTitle: string;
        seoDescription: string;
        tags: string[];
        content: string;
    }> {
        const response = await api.post(
            `${DIRECT_FUNCTIONS_URL}/admin/ai/revise-blog-post`,
            data,
            {
                timeout: 180000,
                headers: getDirectAuthHeaders(),
            }
        );
        return response.data;
    },

    async generateBlogCoverImage(data: {
        title: string;
        excerpt?: string;
        brief?: string;
        tags?: string[];
    }): Promise<{ url: string; model: string; mimeType: string }> {
        const response = await api.post(
            `${DIRECT_FUNCTIONS_URL}/admin/ai/generate-blog-cover-image`,
            data,
            {
                timeout: 180000,
                headers: getDirectAuthHeaders(),
            }
        );
        return response.data;
    },

    async generateBannerImage(data: {
        prompt: string;
        title?: string;
        subtitle?: string;
        buttonText?: string;
        gridSize?: 'large' | 'medium' | 'small';
        referenceImageUrl?: string;
    }): Promise<{ url: string; model: string; mimeType: string }> {
        const response = await api.post(
            `${DIRECT_FUNCTIONS_URL}/admin/ai/generate-banner-image`,
            data,
            {
                timeout: 180000,
                headers: getDirectAuthHeaders(),
            }
        );
        return response.data;
    },
};
